import { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isClickable, isTraitType, resolvePercentDecimals } from '@aytm/app/stats/page/components/lib/utils'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

import useShelfTestState from '@aytm/app/stats/page/question-views/sh/states/useShelfTestState'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import EchoScope from '@aytm/app/stats/page/question-views/components/EchoScope'
import ShelfBlock from '@aytm/app/stats/page/question-views/sh/components/shelf/ShelfBlock'
import ChartBlock from '@aytm/app/stats/page/question-views/sh/components/chart/ChartBlock'

// A shelf-test question is two stacked halves: the shelf rendering with its "Shop Metrics"
// summary, and the per-product chart with its metrics table. Each collapses independently
// and persists its own report params, but they share one model — and one React root.
//
// The shelf half only renders when the question actually carries shelf preview data; the
// old Backbone block gated its construction the same way.
const Block = ({
  id,
  questionKey,
  components,
  answer,
  format,
  isAnimated,
  // report params
  params,
  question,
  modelChartMode,
  globalChartMode,
  globalZoomLevel,
  defaultSortOrder,
  reportPercentDecimals,
  viewClickable,
  confidenceLevels,
  ignoreAmount,
  labelTarget,
  onReady,
  onStateChange,
  // shelf
  shelfData,
  shelfType,
  preview,
  onShelfChartReady,
  // chart
  showPrice,
  onLabelClick,
  ...chartProps
}) => {
  const chartApiRef = useRef(null)

  const {
    viewMode,
    chartCollapsed,
    shelfViewMode,
    shelfCollapsed,
    chartMode,
    zoomLevel,
    sortOrder,
    onModelChange,
    onShelfViewModeChange,
    onShelfCollapsedChange,
    onChartCollapsedChange,
    onChartModeChange,
    onZoomChange,
    onSortChange
  } = useShelfTestState({
    params,
    format,
    question,
    modelChartMode,
    globalChartMode,
    globalZoomLevel,
    answerVariants: chartProps.answerVariants,
    defaultSortOrder,
    answer,
    ignoreAmount,
    confidenceLevels,
    labelTarget,
    chartApiRef,
    onReady,
    onStateChange
  })

  // a clicked column names the order it wants; the state is what settles on one, and the shell
  // writes it down with the rest of the block's params
  const onSort = useCallback(
    (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) onSortChange(order)
    },
    [onSortChange]
  )

  return (
    <BlockWrapper answer={answer}>
      {shelfData && isComponentOn(components, 'shelfBlock') && (
        <EchoScope name="shelf">
          <ShelfBlock
            id={id}
            questionKey={questionKey}
            components={components}
            answer={answer}
            viewMode={viewMode}
            format={format}
            shelfData={shelfData}
            shelfType={shelfType}
            shelfViewMode={shelfViewMode}
            shelfCollapsed={shelfCollapsed}
            chartMode={modelChartMode}
            globalChartMode={globalChartMode}
            preview={preview}
            isAnimated={isAnimated}
            onModelChange={onShelfViewModeChange}
            onShelfCollapsedChange={onShelfCollapsedChange}
            onShelfChartReady={onShelfChartReady}
          />
        </EchoScope>
      )}
      {isComponentOn(components, 'chartBlock') && (
        <EchoScope name="chart">
          <ChartBlock
            id={id}
            questionKey={questionKey}
            components={components}
            answer={answer}
            viewMode={viewMode}
            isAnimated={isAnimated}
            chartCollapsed={chartCollapsed}
            showPrice={showPrice}
            percentDecimals={resolvePercentDecimals(format, reportPercentDecimals)}
            clickable={isClickable(format, viewClickable)}
            onModelChange={onModelChange}
            onChartCollapsedChange={onChartCollapsedChange}
            onLabelClick={onLabelClick}
            onSort={onSort}
            chartCollapsed={chartCollapsed}
            sortOrder={sortOrder}
            chartMode={chartMode}
            modelChartMode={modelChartMode}
            globalChartMode={globalChartMode}
            zoomLevel={zoomLevel}
            globalZoomLevel={globalZoomLevel}
            isTraitType={isTraitType({ ...question, format })}
            ignoreAmount={ignoreAmount}
            confidenceLevels={confidenceLevels}
            onChartModeChange={onChartModeChange}
            onZoomChange={onZoomChange}
            {...chartProps}
            onChartReady={api => {
              chartApiRef.current = api
            }}
          />
        </EchoScope>
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'ShelfTestBlock'

Block.propTypes = {
  id: PropTypes.string,
  questionKey: PropTypes.string,
  components: PropTypes.object,
  answer: answerPropType,
  format: PropTypes.object,
  isAnimated: PropTypes.bool,
  params: PropTypes.object,
  question: PropTypes.object,
  modelChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  globalZoomLevel: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  reportPercentDecimals: PropTypes.number,
  viewClickable: PropTypes.bool,
  confidenceLevels: PropTypes.array,
  ignoreAmount: PropTypes.bool,
  labelTarget: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  shelfData: PropTypes.object,
  shelfType: PropTypes.string,
  preview: PropTypes.object,
  onShelfChartReady: PropTypes.func,
  showPrice: PropTypes.bool,
  onLabelClick: PropTypes.func
}

export default Block
